type Promotion = {
  id: string
  locations?: string[]
  pickupType?: string | null
  weekdays?: string[]
  startTime?: string | null
  endTime?: string | null
  minExpense?: number | null
  customerIds?: string[]
  timesPerPerson?: number
  schedule?: Record<string, { start: string; end: string }[]>
}

type PromotionWithValidation = Promotion & {
  isValid: boolean
  isVisible: boolean
}

export default class PromotionFilter {
  expense: number
  locationId?: string
  weekday: string
  pickupType?: string
  customer?: string
  usedPromotions?: Record<string, number>
  promotionTime: Date

  constructor(
    expense: number,
    locationId?: string,
    weekday?: string,
    pickupType?: string,
    customer?: string,
    usedPromotions?: Record<string, number>,
    promotionTime: Date = new Date()
  ) {
    this.expense = expense
    this.locationId = locationId
    this.weekday =
      weekday ?? promotionTime.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
    this.pickupType = pickupType
    this.customer = customer
    this.usedPromotions = usedPromotions
    this.promotionTime = promotionTime
  }

  addValidation<T extends Promotion>(
    promotions: T[],
    doCustomerChecks = true
  ): (T & PromotionWithValidation)[] {
    return promotions.map(promotion => {
      return {
        ...promotion,
        isValid: this.isPromotionValid(promotion, doCustomerChecks),
        isVisible: this.isPromotionVisible(promotion)
      }
    })
  }

  isPromotionVisible(promotion?: Promotion): boolean {
    if (!promotion) return false

    if ((promotion.locations?.length || 0) > 0) {
      if (!this.locationId || !promotion.locations!.includes(this.locationId)) return false
    }

    if (promotion.pickupType && promotion.pickupType !== this.pickupType) return false

    if ((promotion.weekdays?.length || 0) > 0) {
      if (!promotion.weekdays!.includes(this.weekday!)) return false
    }

    if (!this.checkPromotionSchedule(promotion)) return false

    if (promotion.startTime && this.promotionTime < new Date(promotion.startTime)) return false

    if (promotion.endTime && this.promotionTime > new Date(promotion.endTime)) return false

    return true
  }

  isPromotionValid(promotion?: Promotion, doCustomerChecks = true): boolean {
    if (!promotion) return false

    if (!this.isPromotionVisible(promotion)) return false

    if (this.expense < (promotion.minExpense || 0)) return false

    if (doCustomerChecks) {
      if (
        (promotion.customerIds?.length || 0) > 0 &&
        !promotion.customerIds!.includes(this.customer!)
      ) {
        return false
      }

      if (promotion.timesPerPerson) {
        if (!this.customer) return false

        const promotionUsages = this.usedPromotions?.[promotion.id]
        if (promotionUsages && promotionUsages >= promotion.timesPerPerson) return false
      }
    }

    return true
  }

  private checkPromotionSchedule(promotion: Promotion): boolean {
    if (!promotion.schedule) return true

    const daySchedule = promotion.schedule[this.weekday]
    if (!daySchedule || daySchedule.length === 0) return false
    return daySchedule.some(({ start, end }) => this.isInRange(this.promotionTime, start, end))
  }

  private isInRange(currentTime: Date, start: string, end: string): boolean {
    const [startHour, startMinute] = start.split(':').map(Number)
    const [endHour, endMinute] = end.split(':').map(Number)

    const startMinutes = startHour * 60 + startMinute
    const endMinutes = endHour === 0 && endMinute === 0 ? 24 * 60 : endHour * 60 + endMinute
    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes()

    return currentMinutes >= startMinutes && currentMinutes <= endMinutes
  }
}
